import { Block, Link, PrensaEngine, Typography } from '@xprog/prensa';
import IconButton from 'components/Buttons/IconButton.index';
import GoToHome from 'components/Buttons/CallToActionOutlinedButton.index';
import { useIcon } from 'hooks/useIcon';
import find from 'lodash/find';
import first from 'lodash/first';
import get from 'lodash/get';
import map from 'lodash/map';
import NextLink from 'next/link';
import React, { useContext, useEffect, useState } from 'react';

import Caption from './Caption/Caption.index';
import Byline from './Byline/Byline.index';
import Image from './Image/Image.index';
import Tag from './Tag/Tag.index';
import Texts from './Texts/Texts.index';
import Title from './Title/Title.index';
import Restricted, { BannerDataType } from './Restricted/Restricted.index';
import Subtitle from './Subtitle/Subtitle.index';
import Newsletter from 'components/Newsletter';
import { ArticleProps } from './Article.types';
import * as S from './Article.styled';
import { useRouter } from 'next/router';
import { useImageProxy } from 'hooks/useImageProxy';
import { ServerSidePropsContext } from 'providers/ServerSideProps';
// import ClassiRecomend from '../ClassiRecomend';
import { SessionDataContext } from 'providers/Session';
import { useKeycloak } from '@react-keycloak/ssr';
import { KeycloakInstance } from 'keycloak-js';
import AdsSlot from 'components/Ads/Ads.Slot';

const SNIPPET_SIZE_LIMIT = 800

export const ArticleBody = PrensaEngine.styled('div', {
  align: ['column', 'center', 'top'],
  maxWidth: '100vw',
  '@lg': { maxWidth: 'none' },
  '&.restricted': {
    height: '400px',
    overflow: 'hidden',
  },
});

export const ArticleContainer = PrensaEngine.styled('article', {
  align: ['column', 'center', 'top'],
});


export const ArticleImage = PrensaEngine.styled('img', {
  width: '100%',
  maxWidth: '960px',
});

export const AuthorImage = PrensaEngine.styled('img', {
  borderRadius: '50%',
  marginRight: '$4',
  width: '100%',
  maxWidth: '52px',
});

const convertLocaleDate = (timestamp, showWeekDay, modificationDateEnabled) => {
  const date = new Date(timestamp);
  if (modificationDateEnabled) {
    const options: Intl.DateTimeFormatOptions = {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    };
    return date.toLocaleDateString('pt-BR', options);
  }
  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    ...(showWeekDay && { weekday: 'long' }),
  };
  const dateString = date.toLocaleDateString('pt-BR', options);
  if (showWeekDay) {
    return dateString.replace(/^\w/, (c) => c.toUpperCase());
  }
  return dateString.replace(/(\d{4})(\s+\d{2}:\d{2})/, '$1 |$2');
};

const RenderCaption = (props) => {
  const { captionProps, isFullWidth, textValue, value } = props;
  return (
    <Caption
      {...captionProps}
      isFullWidth={isFullWidth}
      value={value || textValue}
    />
  );
};

const RenderBylineImage = ({ alt, css, linkHref, linkTarget, src }) => {
  const imgSrc = useImageProxy(`${src}&f=1x1`, true);
  const imageTag = () => (
    <AuthorImage {...css} alt={alt} src={imgSrc} width="82px" />
  );
  if (linkHref) {
    return (
      <a href={linkHref} target={linkTarget || '_self'}>
        {imageTag()}
      </a>
    );
  }
  return imageTag();
};

const RenderImage = ({
  alt,
  derivative = '3x2',
  isFullWidth,
  linkHref,
  linkTarget,
  src,
}) => {
  const imgSrc = useImageProxy(`${src}&f=${derivative}`, true);

  const imageTag = () => (
    <ArticleImage
      alt={alt}
      src={imgSrc}
      width={isFullWidth ? '100%' : '960px'}
    />
  );
  if (linkHref) {
    return (
      <a href={linkHref} target={linkTarget || '_self'}>
        {imageTag()}
      </a>
    );
  }
  return imageTag();
};

const RenderTag = ({ children, value }) => (
  <Link
    bypass={false}
    component={NextLink}
    componentProps={{ href: `/tag?tag=${value}`, legacyBehavior: true }}
    href={`/tag?tag=${value}`}
    css={{ fontSize: 0, width: '100%' }}
  >
    {children}
  </Link>
);

const Article: React.FC<ArticleProps> = (props) => {
  const shareSection = get(props, 'shareSection', {
    enabled: false,
    content: [],
  });
  let isPosition = get(props, 'itemPosition', 0);
  let sourceSite = get(props, 'siteOrigem[0]', '');
  const editorialArticle = props.path.split('/')[1] || '';
  const allowedEditorials = ['magazine', 'economia', 'esporte'];

  const isDelete = get(props, 'isDeleted', false);
  const isOnline = get(props, 'isOnline', true);
  const audioLink = get(props, 'audioLink', '');

  const showImageBottom = get(props, 'showImageBottom', false);
  const showImageFull = get(props, 'showImageFull', false);
  const showTopImage = get(props, 'showTopImage', false);
  const showListenButton = false; // get(props, 'showListenButton', true)
  const showMainTitle = get(props, 'showMainTitle', true);
  const tagsList = get(props, 'tags.tags', []);
  const sessionData = useContext(SessionDataContext);
  const { keycloak } = useKeycloak<KeycloakInstance>();

  // const topImage: any = get(props, 'images[0]', false)
  const unlockedIds = get(props, 'sessionUnlockedIds', []);
  const isUnlocked = unlockedIds?.find(
    (i) => i === props.id.replace('policy:', '')
  );
  const serverSideContext = React.useContext(ServerSidePropsContext);
  const isInPreviewMode = get(serverSideContext, 'preview.enabled', false);
  const sitSlug = get(serverSideContext, 'siteSlug', '');
  
  const isViads = allowedEditorials.includes(editorialArticle.toLowerCase()) && isPosition === 0 && sitSlug === 'opopular';

  let [restrictedArticle, setRestrictedArticle] = useState(
    props.sessionPremium
  );

  const router = useRouter();

  const goToHomePage = () => {
    window.location.assign('/')
  };

  const topResources: any[] = get(props, 'images', []);
  const imageTypeBean = 'com.atex.standard.image.ImageContentDataBean';
  const topImage = find(topResources, { type: imageTypeBean });
  const topSelected = showTopImage && first(topResources);

  let bodyRaw = get(props, 'bodyRaw', []);
  let bodyItems = get(props, 'bodyItems', []);
  let textsData = get(props, 'textsData', []);

  const handleOpenArticle = () => {
    let bodyItems = get(props, 'bodyItems', []);
    let textsData = get(props, 'textsData', []);
  };

  const authorBylineProps = {
    ...props.bylineProps?.author,
    value: props?.byline,
  };
  const authorImageProps = {
    ...props.bylineProps?.authorImage,
    imageProps: {
      alt: props?.author?.name || '',
      css: props.bylineProps?.authorImage?.customCss,
      linkHref: '',
      linkTarget: '',
      src: props?.author?.url || '',
    },
  };
  const authorEmailProps = {
    customCss: { fontFamily: '$tertiary', m: '$0', mb: '$1' },
    enabled: get(props, 'email', '') !== '',
    value: get(props, 'email', ''),
  };
  let modificationTime = get(props, 'modificationTime', false);
  modificationTime =
    modificationTime && Number(modificationTime) > 1677570000000
      ? modificationTime
      : false; // remove migration modification event

  const publishingTime = get(props, 'publishingTime', false);
  let modifiedDateEnabled = false;

  if (modificationTime && publishingTime) {
    modifiedDateEnabled = modificationTime > publishingTime;
  }

  const publishedDateProps = {
    enabled: true,
    ...props.bylineProps?.publishedDate,
    value: convertLocaleDate(publishingTime, false, false),
    original: publishingTime,
  };
  const modifiedDateProps = {
    enabled: modifiedDateEnabled,
    ...props.bylineProps?.publishedDate,
    value: modifiedDateEnabled
      ? `Modificado em ${convertLocaleDate(
        modificationTime,
        false,
        modifiedDateEnabled
      )}`
      : '',
    original: modificationTime,
  };
  const topImageProps = showImageFull
    ? {
      ...props.imageProps,
      derivative: 'default',
    }
    : props.imageProps;

  const textBodyImageProps = {
    ...props.imageProps,
    derivative: 'default',
  };
  const shareSectionIcons: any = useIcon(
    shareSection.content.map((i: any) => i.icon)
  );
  const shareSectionData: any = shareSectionIcons.map((i: any) => ({
    ...i,
    config: shareSection.content.find((s: any) => s.icon === i.name),
  }));

  useEffect(() => {
    if (sessionData?.nivelAcesso == 'ASSINANTE' && sessionData?.nivelAcesso) {
      setRestrictedArticle(false);
    }
  }, [sessionData]);

  const renderTopImage = () => {
    return (
      <Image
        alt={props.name}
        byline={get(topImage, 'byline')}
        caption={props?.caption || get(topImage, 'caption')}
        componentImage={RenderImage}
        componentCaption={RenderCaption}
        captionProps={props.captionProps}
        isFullWidth={true}
        imageProps={topImageProps}
        linkHref={get(topImage, 'linkHref')}
        linkTarget={get(topImage, 'linkTarget')}
        src={get(topImage, 'url')}
      />
    );
  };

  if (isDelete || !isOnline || !props.id) {
    return (
      <Block
        css={{
          align: ['column', 'center', 'middle'],
          justifyContent: 'flex-end',
          width: 'auto',
          mt: '24px',
        }}
      >
        <Typography as="p" css={{ fontFamily: '$primary', fontSize: '22px' }}>
          Página 404 - Não encontrado
        </Typography>
        <GoToHome onClick={goToHomePage}>Página inicial</GoToHome>
      </Block>
    );
  }

  const isRestrictedContent = !isUnlocked && restrictedArticle === true;

  return (
    <>
      <S.ArticleContainer
        customCss={props.articleContainer}
        hideInDesktop={props.hideInDesktop}
      >
        <div id="article-text-top"></div>
        <ArticleContainer>
          <S.ArticleContent customCss={props.articleContent}>
            <S.ArticleContentLeft customCss={props.articleContentLeft}>
              {showMainTitle && (
                <Title {...props.titleProps} value={props?.name} />
              )}
              <Subtitle {...props.subtitleProps} value={props?.lead} />
              {shareSection.enabled && (
                <S.ArticleShareSectionContainer>
                  {shareSectionData.map((i: any, k) => (
                    <IconButton
                      key={i.name}
                      href={`${i.config.href}${props.domain}${props.path}`}
                      icon={{ src: i.url, alt: i.config.alt }}
                      linkProps={{
                        target: '_blank',
                        title: i.config.alt,
                        rel: 'noreferrer',
                      }}
                      css={{ button: { mr: '$2' } }}
                    />
                  ))}
                </S.ArticleShareSectionContainer>
              )}
              <Byline
                {...props.bylineProps}
                author={authorBylineProps}
                authorEmail={authorEmailProps}
                authorImage={authorImageProps}
                componentImage={RenderBylineImage}
                modifiedDate={modifiedDateProps}
                publishedDate={publishedDateProps}
                showListenButton={showListenButton}
              />

              {!showImageBottom &&
                topImage &&
                topSelected === topImage &&
                renderTopImage()}
              <ArticleBody className={isRestrictedContent ? 'restricted' : ''}>
                <Texts
                  componentCaption={RenderCaption}
                  componentImage={RenderImage}
                  captionProps={props.captionProps}
                  imageProps={textBodyImageProps}
                  image={Image}
                  items={bodyItems}
                  isViads={isViads}
                  isPosition={isPosition}
                  sourceSite={sourceSite}
                  siteDomain={serverSideContext.site.siteDomain}
                  textProps={props?.textProps}
                  isRestricted={isRestrictedContent}
                  audioLink={audioLink}
                />
                {map(textsData, (item: any, key) => {
                  let itemBodyItems = item?.bodyItems;
                  // if (!isUnlocked && restrictedArticle) {
                  //   debugger
                  //   itemBodyItems = itemBodyItems?.slice(0, 2);
                  // }
                  return (
                    <Texts
                      componentCaption={RenderCaption}
                      componentImage={RenderImage}
                      captionProps={props.captionProps}
                      itemsAheadBody={item?.itemsAheadBody || []}
                      imageProps={textBodyImageProps}
                      image={Image}
                      images={item?.images || []}
                      items={itemBodyItems || []}
                      textProps={props?.textProps}
                      key={key}
                      isRestricted={isRestrictedContent}
                    />
                  );
                })}
                {showImageBottom &&
                  topSelected === topImage &&
                  topImage &&
                  renderTopImage()}
              </ArticleBody>
              {!isUnlocked && restrictedArticle == true && (
                <Restricted
                  articleCid={props.id}
                  trialStage={
                    !sessionData && !keycloak?.authenticated
                      ? 1
                      : (sessionData &&
                        sessionData?.inscricaoDegustador
                          ?.creditosDegustadorList[0]?.quantidade <= 0) ||
                        (!sessionData?.inscricaoDegustador &&
                          !sessionData?.numeroContrato)
                        ? 0
                        : 2
                  }
                  setRestrictedArticle={setRestrictedArticle}
                  handleOpenArticle={handleOpenArticle}
                  author={authorBylineProps.value}
                  publishedDate={publishedDateProps.value}
                  articleType={props.articleType}
                />
              )}
              {shareSection.enabled && (
                <S.ArticleShareSectionContainer>
                  {shareSectionData.map((i: any, k) => (
                    <IconButton
                      key={i.name}
                      href={`${i.config.href}${props.domain}${props.path}`}
                      icon={{ src: i.url, alt: i.config.alt }}
                      linkProps={{
                        target: '_blank',
                        title: i.config.alt,
                        rel: 'noreferrer',
                      }}
                      css={{ button: { mr: '$2' } }}
                    />
                  ))}
                </S.ArticleShareSectionContainer>
              )}
              <Block
                css={{
                  align: ['column', 'center', 'middle'],
                  width: 'auto',
                  mb: '$6',
                }}
              >
                {sitSlug === 'opopular' && (
                  <>
                    <AdsSlot type='newsletter' componentReference='top' data={{
                      name: 'Article Newsletter'
                    }} />
                    <Newsletter />
                    <AdsSlot type='newsletter' componentReference='bottom' data={{
                      name: 'Article Newsletter'
                    }} />
                  </>
                )}
              </Block>
              {tagsList.length > 0 && (
                <S.ArticleTagContainer customCss={props?.articleTagsContainer}>
                  {map(tagsList, (tag, key) => {
                    return <Tag value={tag} key={key} wrapLink={RenderTag} />;
                  })}
                </S.ArticleTagContainer>
              )}
              {/* FIXME rm classi <div style={{ marginLeft: '-16px' }}>
              <ClassiRecomend />
            </div>*/}
            </S.ArticleContentLeft>
            <S.ArticleContentRight customCss={props.articleContentRight} />
          </S.ArticleContent>
        </ArticleContainer>
        <div id="article-text-bottom"></div>
      </S.ArticleContainer>
    </>
  );
};

export default Article;
